import type { Cents } from "../domain/money";
import type { FinancialBucket } from "../domain/buckets";
import type { CostBasis, ReconciliationResult } from "./reconciliation";

/**
 * PROJECT FINANCIAL CONTROL (Phase 12, spec §41A) — pure and deterministic.
 *
 * Seven numbers, defined once and never used interchangeably:
 *
 *   ORIGINAL BASELINE        the frozen initial approved rehab expectation
 *   APPROVED CHANGE ORDERS   the net approved post-baseline delta
 *   AUTHORIZED REHAB BUDGET  baseline + approved change orders funded from
 *                            the budget (contingency-funded changes are
 *                            already inside the frozen contingency)
 *   CURRENT WORKING ESTIMATE what the active plan predicts right now
 *   ACTUAL RECORDED COST     money real transactions have already recorded
 *   PROJECTED FINAL COST     what the project will most likely cost when
 *                            finished, per cost basis, using Phase 9C
 *                            completeness — NEVER actual + proposed
 *   COMMITTED COST           deliberately NOT computed: the purchase model
 *                            has no payment status, so it cannot be derived
 *                            honestly yet (see COMMITTED_COST_SEAM)
 *
 * Actual truth always comes from the recorded transactions; nothing here is
 * stored, and no "final actual" total is ever persisted.
 */

/**
 * Why Committed Cost is absent rather than approximated. "Committed" must
 * mean money we are contractually on the hook for but have not paid —
 * which needs a payment/invoice status the Purchase model does not carry.
 * Proposed cost is NOT committed cost.
 */
export const COMMITTED_COST_SEAM =
  "Unpaid purchase and invoice balances are not inferred: purchase records have no paid/unpaid status. Dated bills and loan obligations are separate commitments, not actual costs merely because they are scheduled.";

export type ProjectionRule =
  /** The basis is finished: actual replaces the estimate. */
  | "actual-complete"
  /** Part recorded: actual plus the remaining estimate for the rest. */
  | "actual-plus-remaining"
  /** Nothing recorded yet: the current estimate stands. */
  | "estimate-only"
  /** Settled through a labor group: the group's one actual is authoritative. */
  | "settled-group"
  /** Money recorded that no basis can claim; carried, never netted away. */
  | "unattributed-actual";

export interface ProjectedBasisLine {
  featureId: string | null;
  featureName: string;
  scopeLabel: string;
  categoryId: string | null;
  basis: CostBasis | null;
  proposedCents: Cents;
  actualCents: Cents;
  /** The remaining estimate for unfinished work (never negative). */
  remainingCents: Cents;
  projectedCents: Cents;
  rule: ProjectionRule;
  /** One sentence a person can check the number against. */
  explanation: string;
}

export interface ProjectedRehab {
  lines: ProjectedBasisLine[];
  /** Σ projected across every basis, group and unattributed amount. */
  projectedCents: Cents;
  /** Σ actual recorded against renovation scope (for reference only). */
  actualCents: Cents;
  /** Σ current proposed (for reference only). */
  proposedCents: Cents;
  byBasis: Record<CostBasis, { proposedCents: Cents; actualCents: Cents; projectedCents: Cents }>;
  /** True while any basis is still unfinished — a final variance is not yet real. */
  incomplete: boolean;
  incompleteCount: number;
}

const EMPTY_BASIS = (): Record<CostBasis, { proposedCents: Cents; actualCents: Cents; projectedCents: Cents }> => ({
  material: { proposedCents: 0, actualCents: 0, projectedCents: 0 },
  labor: { proposedCents: 0, actualCents: 0, projectedCents: 0 },
  other: { proposedCents: 0, actualCents: 0, projectedCents: 0 },
});

/**
 * PROJECTED FINAL REHAB, per feature and cost basis.
 *
 * The rule per basis, from Phase 9C completeness:
 *   complete          → actual (the work is done; the estimate is history)
 *   partial           → actual + max(0, proposed − actual)
 *   not-started       → proposed
 *   settled-in-group  → 0 here; the group contributes its own actual once
 *   not-comparable    → the estimate stands AND the unattributable actual is
 *                       carried separately, never silently merged
 *
 * Actual recorded against the project but against no feature at all (site
 * overheads, dumpsters booked to the project) is added once, explicitly.
 */
export interface UnlinkedProposedLine {
  lineId: string;
  description: string;
  categoryId: string | null;
  basis: CostBasis | null;
  proposedCents: Cents;
  /** Actual money linked directly to this line (never to a feature). */
  actualCents: Cents;
}

export function projectRehab(
  reconciliation: ReconciliationResult,
  options: { unassignedActualCents?: Cents; unlinkedLines?: UnlinkedProposedLine[] } = {},
): ProjectedRehab {
  const lines: ProjectedBasisLine[] = [];
  const byBasis = EMPTY_BASIS();
  let incompleteCount = 0;

  for (const feature of reconciliation.features) {
    for (const basis of feature.bases) {
      const proposed = basis.proposedCents;
      const actual = basis.recordedActualCents;
      let projected = 0;
      let remaining = 0;
      let rule: ProjectionRule;
      let explanation: string;

      switch (basis.completeness) {
        case "complete":
          if (actual === 0 && proposed > 0) {
            // Marked finished with nothing recorded: the app will not claim
            // the work was free. The estimate stands until money appears.
            projected = proposed;
            incompleteCount += 1;
            rule = "estimate-only";
            explanation = `${label(basis.basis)} is marked complete but no money is recorded against it, so the ${money(proposed)} estimate still stands — record the cost, or mark it crew-supplied / not required.`;
            break;
          }
          projected = actual;
          rule = "actual-complete";
          explanation = `${label(basis.basis)} is complete, so the actual ${money(actual)} replaces the ${money(proposed)} estimate.`;
          break;
        case "settled-in-group":
          projected = 0;
          rule = "settled-group";
          explanation = `${label(basis.basis)} is financially settled inside a work group; the group's actual is counted once, not per item.`;
          break;
        case "partial":
          remaining = Math.max(0, proposed - actual);
          projected = actual + remaining;
          incompleteCount += 1;
          rule = "actual-plus-remaining";
          explanation =
            remaining > 0
              ? `${label(basis.basis)} is part recorded: ${money(actual)} so far plus ${money(remaining)} still expected.`
              : `${label(basis.basis)} is part recorded and already at or above the estimate, so ${money(actual)} stands.`;
          break;
        case "not-comparable":
          projected = proposed;
          incompleteCount += 1;
          rule = "estimate-only";
          explanation = `${label(basis.basis)} has money recorded that no basis can claim, so the ${money(proposed)} estimate still stands and that money is carried separately.`;
          break;
        default:
          projected = proposed;
          if (proposed > 0) incompleteCount += 1;
          rule = "estimate-only";
          explanation = `Nothing recorded for ${label(basis.basis).toLowerCase()} yet, so the ${money(proposed)} estimate stands.`;
      }

      if (proposed === 0 && actual === 0 && projected === 0) continue;
      lines.push({
        featureId: feature.featureId,
        featureName: feature.featureName,
        scopeLabel: feature.scopeLabel,
        categoryId: feature.categoryId,
        basis: basis.basis,
        proposedCents: proposed,
        actualCents: actual,
        remainingCents: remaining,
        projectedCents: projected,
        rule,
        explanation,
      });
      byBasis[basis.basis].proposedCents += proposed;
      byBasis[basis.basis].actualCents += actual;
      byBasis[basis.basis].projectedCents += projected;
    }
  }

  // Grouped labor settlements: ONE actual for the whole group (§31A).
  for (const settlement of reconciliation.settlements) {
    const projected = Math.max(settlement.actualCents, 0);
    lines.push({
      featureId: null,
      featureName: settlement.description,
      scopeLabel: settlement.coverageKind === "mixed-work" ? "Mixed contractor work group" : "Labor group",
      categoryId: null,
      basis: settlement.coverageKind === "mixed-work" ? null : "labor",
      proposedCents: settlement.proposedCents,
      actualCents: settlement.actualCents,
      remainingCents: 0,
      projectedCents: projected,
      rule: "settled-group",
      explanation: `Financially settled as one total of ${money(settlement.actualCents)} covering ${settlement.proposedLineIds.length} estimate line(s). Physical completion is a separate decision.`,
    });
    if(settlement.coverageKind !== "mixed-work") {
      byBasis.labor.proposedCents += settlement.proposedCents;
      byBasis.labor.actualCents += settlement.actualCents;
      byBasis.labor.projectedCents += projected;
    }
  }

  // Estimate lines that belong to no feature (a quick manual estimate) are
  // still scope: they are projected the same way, with any money linked
  // directly to them counted once.
  for (const line of options.unlinkedLines ?? []) {
    const remaining = Math.max(0, line.proposedCents - line.actualCents);
    const projected = line.actualCents > 0 ? line.actualCents + remaining : line.proposedCents;
    const rule: ProjectionRule = line.actualCents > 0 ? "actual-plus-remaining" : "estimate-only";
    if (rule === "actual-plus-remaining" || line.proposedCents > 0) incompleteCount += 1;
    lines.push({
      featureId: null,
      featureName: line.description,
      scopeLabel: "Project estimate line",
      categoryId: line.categoryId,
      basis: line.basis,
      proposedCents: line.proposedCents,
      actualCents: line.actualCents,
      remainingCents: remaining,
      projectedCents: projected,
      rule,
      explanation:
        line.actualCents > 0
          ? `${money(line.actualCents)} recorded against this line so far, plus ${money(remaining)} still expected.`
          : `Nothing recorded against this line yet, so the ${money(line.proposedCents)} estimate stands.`,
    });
    if (line.basis) {
      byBasis[line.basis].proposedCents += line.proposedCents;
      byBasis[line.basis].actualCents += line.actualCents;
      byBasis[line.basis].projectedCents += projected;
    }
  }

  const unassigned = options.unassignedActualCents ?? reconciliation.unassignedActualCents;
  if (unassigned !== 0) {
    lines.push({
      featureId: null,
      featureName: "Recorded against the project, not a feature",
      scopeLabel: "Project",
      categoryId: null,
      basis: null,
      proposedCents: 0,
      actualCents: unassigned,
      remainingCents: 0,
      projectedCents: unassigned,
      rule: "unattributed-actual",
      explanation: `${money(unassigned)} of renovation money is recorded on the project without a feature — real spend, counted once.`,
    });
  }

  const projectedCents = lines.reduce((sum, l) => sum + l.projectedCents, 0);
  return {
    lines,
    projectedCents,
    actualCents: reconciliation.totals.recordedActualCents,
    proposedCents: reconciliation.totals.proposedCents,
    byBasis,
    incomplete: incompleteCount > 0,
    incompleteCount,
  };
}

/* ------------------------------------------------------------------ */
/* Budget health                                                       */
/* ------------------------------------------------------------------ */

export interface RehabControl {
  baselineCents: Cents;
  approvedChangeCents: Cents;
  /** Contingency-funded portion of approved change orders. */
  contingencyFundedCents: Cents;
  authorizedCents: Cents;
  initialContingencyCents: Cents;
  remainingContingencyCents: Cents;
  currentWorkingCents: Cents;
  projectedFinalCents: Cents;
  actualToDateCents: Cents;
  /** projected − authorized: the number that matters for control. */
  projectedVarianceCents: Cents;
  projectedVariancePctX100: number | null;
  /** current − authorized: planning that has not been authorized. */
  unapprovedPlanningCents: Cents;
  hasBaseline: boolean;
  projectionIncomplete: boolean;
}

/**
 * Budget health. `projectedVariance` and `unapprovedPlanning` are different
 * questions and are never merged: one is "will we overrun what was
 * authorized", the other is "have I planned changes nobody approved".
 */
export function rehabControl(input: {
  baselineCents: Cents;
  approvedChangeCents: Cents;
  contingencyFundedCents: Cents;
  initialContingencyCents: Cents;
  currentWorkingCents: Cents;
  projectedFinalCents: Cents;
  actualToDateCents: Cents;
  hasBaseline: boolean;
  projectionIncomplete: boolean;
}): RehabControl {
  const authorized = input.hasBaseline
    ? input.baselineCents + input.approvedChangeCents
    : input.currentWorkingCents;
  const remainingContingency = Math.max(0, input.initialContingencyCents - input.contingencyFundedCents);
  return {
    baselineCents: input.baselineCents,
    approvedChangeCents: input.approvedChangeCents,
    contingencyFundedCents: input.contingencyFundedCents,
    authorizedCents: authorized,
    initialContingencyCents: input.initialContingencyCents,
    remainingContingencyCents: remainingContingency,
    currentWorkingCents: input.currentWorkingCents,
    projectedFinalCents: input.projectedFinalCents,
    actualToDateCents: input.actualToDateCents,
    projectedVarianceCents: input.projectedFinalCents - authorized,
    projectedVariancePctX100: pctX100(input.projectedFinalCents - authorized, authorized),
    unapprovedPlanningCents: input.hasBaseline ? input.currentWorkingCents - authorized : 0,
    hasBaseline: input.hasBaseline,
    projectionIncomplete: input.projectionIncomplete,
  };
}

/* ------------------------------------------------------------------ */
/* Sale, profit and return                                             */
/* ------------------------------------------------------------------ */

export interface SaleInput {
  saleDateISO: string | null;
  grossSalePriceCents: Cents;
  commissionCents: Cents;
  sellerClosingCents: Cents;
  concessionsCents: Cents;
  otherSellingCents: Cents;
  /**
   * True when those selling costs are ALSO recorded as actual expenses in
   * the selling bucket. Then accounting is the authority and the sale
   * record's figures are documentation only — never counted twice.
   */
  costsRecordedInAccounting: boolean;
}

export interface SaleResult {
  grossSalePriceCents: Cents;
  /** The deductions actually used to compute net proceeds. */
  sellingDeductionsCents: Cents;
  netProceedsCents: Cents;
  /** Selling costs found in the accounting records (bucket total). */
  recordedSellingCents: Cents;
  /** Set when both sources hold money and one had to be chosen. */
  doubleCountWarning: string | null;
  source: "sale-record" | "accounting";
}

/**
 * Net proceeds, without double counting selling costs.
 *
 * Selling costs live in exactly one place for the maths: either the sale
 * record (typical — entered from the closing statement) or the accounting
 * records (when each cost was booked as an expense). When both hold money
 * the app says so instead of quietly picking.
 */
export function saleResult(sale: SaleInput, recordedSellingCents: Cents): SaleResult {
  const entered =
    sale.commissionCents + sale.sellerClosingCents + sale.concessionsCents + sale.otherSellingCents;
  const useAccounting = sale.costsRecordedInAccounting;
  const deductions = useAccounting ? recordedSellingCents : entered;
  let warning: string | null = null;
  if (!useAccounting && recordedSellingCents !== 0 && entered !== 0) {
    warning = `Selling costs appear twice: ${money(entered)} on the sale record and ${money(recordedSellingCents)} in the accounting records. The sale record is being used; tick "already recorded in accounting" if the expenses are the real ones.`;
  }
  if (useAccounting && entered !== 0 && recordedSellingCents === 0) {
    warning = `The sale record lists ${money(entered)} of selling costs but none are recorded in accounting, so nothing was deducted. Record them as expenses or untick the box.`;
  }
  return {
    grossSalePriceCents: sale.grossSalePriceCents,
    sellingDeductionsCents: deductions,
    netProceedsCents: sale.grossSalePriceCents - deductions,
    recordedSellingCents,
    doubleCountWarning: warning,
    source: useAccounting ? "accounting" : "sale-record",
  };
}

export interface ProjectCostBreakdown {
  purchasePriceCents: Cents;
  acquisitionCostsCents: Cents;
  rehabCents: Cents;
  holdingCents: Cents;
  financingCents: Cents;
  sellingCents: Cents;
  otherCents: Cents;
  totalCents: Cents;
  /** True when the rehab figure is a projection rather than final. */
  rehabIsProjected: boolean;
}

/**
 * Total project cost. The rehab component is the PROJECTED final (or the
 * final actual once everything is complete); every other bucket is actual
 * money only — Berrier Reno has no holding/financing forecast model and
 * does not invent one.
 */
export function projectCost(input: {
  purchasePriceCents: Cents;
  acquisitionCostsCents: Cents;
  rehabCents: Cents;
  rehabIsProjected: boolean;
  holdingCents: Cents;
  financingCents: Cents;
  sellingCents: Cents;
  otherCents: Cents;
}): ProjectCostBreakdown {
  const total =
    input.purchasePriceCents +
    input.acquisitionCostsCents +
    input.rehabCents +
    input.holdingCents +
    input.financingCents +
    input.sellingCents +
    input.otherCents;
  return { ...input, totalCents: total };
}

export interface ReturnMetrics {
  netProfitCents: Cents;
  /** Net profit ÷ total project cost, hundredths of a percent. */
  roiPctX100: number | null;
  /** Formula in words — every metric states what it is. */
  roiFormula: string;
  annualizedPctX100: number | null;
  annualizedFormula: string | null;
  holdingDays: number | null;
  /** True while any input is a forecast rather than a settled number. */
  isForecast: boolean;
}

/**
 * Profit and return.
 *
 * profit = net proceeds − total project cost (which already includes the
 * purchase price; the price is never counted twice, and acquisition
 * EXPENSES are separate from the price itself).
 */
export function returnMetrics(input: {
  netProceedsCents: Cents;
  totalProjectCostCents: Cents;
  purchaseDateISO: string | null;
  saleDateISO: string | null;
  isForecast: boolean;
}): ReturnMetrics {
  const profit = input.netProceedsCents - input.totalProjectCostCents;
  const roi = pctX100(profit, input.totalProjectCostCents);
  const days = daysBetween(input.purchaseDateISO, input.saleDateISO);
  let annualized: number | null = null;
  let annualizedFormula: string | null = null;
  if (roi !== null && days !== null && days >= 30 && input.totalProjectCostCents > 0) {
    annualized = Math.round((roi * 365) / days);
    annualizedFormula = `ROI × 365 ÷ ${days} days held. Short holds produce very large annual figures — read it beside the plain ROI, not instead of it.`;
  }
  return {
    netProfitCents: profit,
    roiPctX100: roi,
    roiFormula:
      "Net profit ÷ total project cost (purchase price, acquisition, the rehab or construction work, holding, financing, selling and other).",
    annualizedPctX100: annualized,
    annualizedFormula,
    holdingDays: days,
    isForecast: input.isForecast,
  };
}

/* ------------------------------------------------------------------ */
/* Rollups                                                             */
/* ------------------------------------------------------------------ */

export interface RollupRow {
  key: string;
  label: string;
  proposedCents: Cents;
  actualCents: Cents;
  projectedCents: Cents;
  /** projected − proposed; only meaningful alongside `incomplete`. */
  varianceCents: Cents;
  incomplete: boolean;
}

/**
 * One financial truth, several views: the same projected lines rolled up by
 * room, category, feature or cost basis. No screen computes its own maths.
 */
export function rollup(
  projected: ProjectedRehab,
  dimension: "room" | "category" | "feature" | "basis",
  labels: { categories?: Map<string, string> } = {},
): RollupRow[] {
  const rows = new Map<string, RollupRow>();
  for (const line of projected.lines) {
    const { key, label: rowLabel } = rollupKey(line, dimension, labels);
    const existing =
      rows.get(key) ??
      { key, label: rowLabel, proposedCents: 0, actualCents: 0, projectedCents: 0, varianceCents: 0, incomplete: false };
    existing.proposedCents += line.proposedCents;
    existing.actualCents += line.actualCents;
    existing.projectedCents += line.projectedCents;
    existing.varianceCents = existing.projectedCents - existing.proposedCents;
    if (line.rule === "actual-plus-remaining" || line.rule === "estimate-only") existing.incomplete = true;
    rows.set(key, existing);
  }
  return [...rows.values()].sort((a, b) => b.projectedCents - a.projectedCents || a.label.localeCompare(b.label));
}

function rollupKey(
  line: ProjectedBasisLine,
  dimension: "room" | "category" | "feature" | "basis",
  labels: { categories?: Map<string, string> },
): { key: string; label: string } {
  switch (dimension) {
    case "room":
      return { key: line.scopeLabel || "Project", label: line.scopeLabel || "Project" };
    case "category":
      return {
        key: line.categoryId ?? "uncategorized",
        label: line.categoryId ? (labels.categories?.get(line.categoryId) ?? line.categoryId) : "Uncategorized",
      };
    case "basis":
      return { key: line.basis ?? "unattributed", label: line.basis ? label(line.basis) : "Not attributable" };
    default:
      return { key: line.featureId ?? line.featureName, label: line.featureName };
  }
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

export function pctX100(numerator: Cents, denominator: Cents): number | null {
  if (denominator === 0) return null;
  const scaled = (numerator * 10000) / denominator;
  return scaled >= 0 ? Math.floor(scaled + 0.5) : -Math.floor(-scaled + 0.5);
}

export function daysBetween(fromISO: string | null, toISO: string | null): number | null {
  if (!fromISO || !toISO) return null;
  const from = Date.parse(fromISO);
  const to = Date.parse(toISO);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

export const BUCKET_ORDER: FinancialBucket[] = [
  "acquisition",
  "renovation",
  "holding",
  "financing",
  "selling",
  "other",
];

function label(basis: CostBasis): string {
  return basis.charAt(0).toUpperCase() + basis.slice(1);
}

function money(cents: Cents): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100).toLocaleString("en-US")}.${String(abs % 100).padStart(2, "0")}`;
}
